import { crestPickSuppressedAtom } from '@/atoms/crestLine.atom';
import { elevationProfileToolModeAtom } from '@/atoms/elevationProfile.atom';
import { pipeToolModeAtom } from '@/atoms/pipe.atom';
import { stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import {
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
} from '@/atoms/viewer.atom';
import { atom } from 'jotai';

/**
 * True while some tool in the 3D scene owns the left click — i.e. a click is
 * placing a vertex / seed point, not selecting a model for inspection.
 *
 * Every canvas pick handler that opens a properties drawer must bail on this.
 * R3F's object handlers fire *before* the DOM-level `pointerup` / `click` the
 * picking tools listen on, so without the guard a single click both drops a
 * vertex on the tool AND pops a drawer open over the scene the user is
 * drawing on.
 *
 * Phases are deliberately asymmetric: tools that have finished picking
 * (`adjusting`, `closed`, `review`) leave selection working, so the user can
 * still tap nearby geometry for context while reviewing a draft. That mirrors
 * the long-standing guard list in `GeoJson.tsx`, of which this atom is the
 * shared definition.
 *
 * `crestPickSuppressedAtom` is folded in rather than checked separately — one
 * definition of "picking is suppressed", per the crest draw-guide fix.
 */
export const toolPickSuppressedAtom = atom((get) => {
  if (get(crestPickSuppressedAtom)) return true;
  if (get(addPointModeAtom).enabled) return true;

  const surface = get(addSurfaceModeAtom);
  if (surface.enabled && surface.phase !== 'adjusting') return true;

  const line = get(addLineModeAtom);
  if (line.enabled && line.phase === 'picking') return true;

  const polygon = get(addPolygonModeAtom);
  if (polygon.enabled && polygon.phase === 'picking') return true;

  if (get(elevationProfileToolModeAtom).phase === 'drawing') return true;

  const stockpile = get(stockpileToolModeAtom);
  if (stockpile.enabled && stockpile.phase !== 'closed') return true;

  const pipe = get(pipeToolModeAtom);
  if (pipe.enabled && pipe.phase === 'seeking') return true;

  return false;
});

/**
 * The distance / area measuring tools are driven by URL search params rather
 * than atoms, so they can't live in `toolPickSuppressedAtom`. Pick handlers
 * that run outside React's render (imperative DOM/R3F callbacks) can call this
 * with no argument — `window.location.search` is always current at click time,
 * which avoids subscribing a heavy 3D component to every URL change.
 */
export const isMeasurementToolActive = (search: string = window.location.search): boolean => {
  const params = new URLSearchParams(search);
  return params.get('showMeasurements') === 'true' || params.get('showAreaMeasurement') === 'true';
};
